/* Flow profiles — the form-side variation point for the three application
   types (direct consumer, broker, commercial asset finance).

   A profile bundles everything channel/product-specific the flow needs:
   steps, initial state, per-panel copy overrides, validator overrides. The
   direct profile reproduces the historical form exactly; broker and
   commercial differences plug in here as the business rules arrive, without
   touching the shared step machinery.

   Mirror of the server side: server/lib/profiles.js carries the matching
   validation/editable-field registry (HTTP boundary — deliberately not
   imported from here). */
import { INITIAL, getSteps, getProduct } from "./data.js";

export const CHANNELS = ["direct", "broker", "internal"];
const normalizeChannel = (c) => (CHANNELS.includes(c) ? c : "direct");

/* PLACEHOLDER broker extras: a reference the broker can match to their own
   CRM, and the authority declaration shown on the review step. The real
   broker field set replaces/extends these when the business rules land. */
const CHANNEL_INITIAL = {
  broker: { brokerReference: "", consentBrokerDeclaration: false },
};

/* PLACEHOLDER commercial extras (the entity step's fields). */
const PRODUCT_INITIAL = {
  commercial: {
    // The entity itself. Everything from abnVerified down is what the
    // Australian Business Register told us when the ABN was validated -
    // captured so the assessor can see WHAT was checked and when, not just
    // the name that came back. abnTooNew null = not checked, not "fine".
    abn: "", entityName: "", entityType: "", tradingNames: [],
    abnVerified: false, abnStatus: "", abnActiveSince: "", abnAgeMonths: null,
    abnTooNew: null, gstRegistered: null,
    // "Your business situation" — what it does and how it runs
    anzsicCode: "", anzsicLabel: "", businessActivity: "",
    paymentMethods: [], businessPremises: "", employeeCount: "",
    taxPosition: "",
    // Directors / sole traders / partners / trustees / guarantors. Exactly
    // one carries isPrimary — that person's contact details are the ones the
    // platform emails, and they're mirrored to the top-level lead fields.
    parties: [],
    // Business financials — asset finance replaces the consumer income &
    // expenses step with the trading figures an owner can actually answer
    // without their accountant on the phone. Gross and net profit are NOT
    // among them (dropped from intake 2026-08-16): they stay editable in the
    // console, where the assessor enters them from the real financials.
    annualTurnover: "",
    tradingTrend: "", tradingNote: "",
    // Drawings: a basis ("a set amount" / "whatever's left over") and, for
    // the first, the amount. Both optional.
    ownerDrawingsBasis: "", ownerDrawingsMonthly: "",
    // Balloon (residual) percentage - 0 means none, 30 is the ceiling.
    balloonPct: 0,
    // Bank step: statements the broker already holds. statementsSupplied is
    // set once a file has actually landed server-side; illionDocumentId is
    // illion's own reference for the report they ran.
    statementsSupplied: false, statementsFileName: "", illionDocumentId: "",
    // Commercial document set — its own consent fields, so acceptance is
    // provably against the business documents and never the consumer ones
    // (src/data/consents.jsx; public/commercial-privacy-consent/).
    consentCommercialPrivacy: false, consentElectronic: false,
  },
};

export function getFlowProfile({ product = "car", channel = "direct" } = {}) {
  const ch = normalizeChannel(channel);
  const productCfg = getProduct(product);
  return {
    product: productCfg.id,
    channel: ch,
    productCfg,
    // Per-channel step injection/removal lands here (e.g. the commercial
    // entity step); today every channel shares the product's step list.
    steps: getSteps(productCfg.id),
    initial: { ...INITIAL, ...(PRODUCT_INITIAL[productCfg.id] || {}), ...(CHANNEL_INITIAL[ch] || {}) },
    // Per-panel copy overrides, keyed by panel key — the broker "you" ->
    // "your client" rewrite goes here. Empty = the panel's built-in copy.
    copy: {},
    // Validator overrides (PHONE_OK / EMAIL_OK / DOB_OK). Empty = built-ins.
    validators: {},
  };
}
